import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";

// @mui material components
import {
  Card,
  CircularProgress,
  Fade,
  Grid,
  Icon,
  InputLabel,
  Modal,
  Tooltip,
  Autocomplete,
  TextField,
} from "@mui/material";

import { gql, useLazyQuery, useMutation } from "@apollo/client";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "50%", xxl: "40%" },
  overflowY: "auto",
  p: 3,
};

export default function ModalResponsable({
  open,
  handleClose,
  currentResponsableId,
  idEmpresa,
  refetch,
}) {
  const [responsables, setResponsables] = useState([]);
  const [selectedResponsable, setSelectedResponsable] = useState(null);
  const { handleSnackbar } = useContext(MessageManager);

  const [getResponsablesTecnicos, { loading: loadingResponsables }] = useLazyQuery(
    gql`
      query getResponsablesTecnicos {
        responsablesTecnicos {
          id
          nombre
          apellido
          email
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
      onCompleted: (data) => {
        setResponsables(data.responsablesTecnicos || []);
        if (currentResponsableId) {
          const current = data.responsablesTecnicos.find(
            (responsable) => responsable.id === currentResponsableId
          );
          setSelectedResponsable(current || null);
        }
      },
      onError: (error) => {
        handleSnackbar(error.message || "Error al cargar los responsables técnicos", "error");
      },
    }
  );

  const [saveEmpresa, { loading: loadingSave }] = useMutation(
    gql`
      mutation saveEmpresa($input: EmpresaInput!) {
        saveEmpresa(input: $input) {
          id
        }
      }
    `
  );
  const handleSave = () => {
    if (selectedResponsable) {
      saveEmpresa({
        variables: {
          input: {
            id: idEmpresa,
            idResponsable: selectedResponsable.id,
          },
        },
      })
        .then(() => {
          refetch();
          handleSnackbar("Responsable técnico asignado correctamente", "success");
          handleClose();
        })
        .catch((e) => {
          handleSnackbar(e.message || "Error al asignar el responsable técnico", "error");
        });
    } else {
      handleSnackbar("Debe seleccionar un responsable técnico", "warning");
    }
  };

  useEffect(() => {
    if (open) {
      getResponsablesTecnicos();
    }
  }, [open]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center">
              <SoftTypography variant="h6">Seleccionar Responsable Técnico</SoftTypography>
              <Tooltip title="Cerrar" placement="top">
                <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                  close
                </Icon>
              </Tooltip>
            </SoftBox>
            <SoftBox py={3}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <InputLabel htmlFor="responsableTecnico">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Responsable Técnico
                      <SoftTypography variant="caption" fontWeight="bold" color="primary">
                        &nbsp;*
                      </SoftTypography>
                    </SoftTypography>
                  </InputLabel>
                  <Autocomplete
                    id="responsableTecnico"
                    options={responsables}
                    getOptionLabel={(option) =>
                      `${option.nombre} ${option.apellido} (${option.email})`
                    }
                    value={selectedResponsable}
                    onChange={(event, newValue) => setSelectedResponsable(newValue)}
                    loading={loadingResponsables}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Seleccione un responsable técnico"
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingResponsables ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </SoftBox>
            <SoftBox display="flex" justifyContent="flex-end" alignItems="center">
              <SoftButton color="primary" onClick={handleClose} sx={{ marginRight: 1 }}>
                <Icon sx={{ fontWeight: "light" }}>cancel</Icon>
                &nbsp;Cancelar
              </SoftButton>
              <SoftButton
                color="gobSecondary"
                disabled={loadingResponsables || !selectedResponsable}
                onClick={handleSave}
              >
                <Icon sx={{ fontWeight: "light" }}>save</Icon>
                &nbsp;Guardar
              </SoftButton>
            </SoftBox>
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalResponsable.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  idEmpresa: PropTypes.string,
  currentResponsableId: PropTypes.string,
  refetch: PropTypes.func,
};
